'use client';

import { useTranslations } from 'next-intl';
import { Heart, Share } from 'lucide-react';
import { Button, IconButton } from '@zillow/constellation-10';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { LucideIcon } from '@/src/components/LucideIcon';
import { cx } from '@/styled-system/css';
import { useShareAction } from '@/src/lib/hooks/useShareAction';
import { useListingCTAs, useListingSaveActions } from '@/src/components/features/property/hooks';
import {
  headerActionButton,
  headerCtaButton,
  headerIconButton,
} from '@/src/components/features/property/ListingPage/desktopStickyHeader.styles';
import { mobileHeaderMediaButton } from '@/src/components/features/property/ListingPage/listingPage.styles';
import type { ExtendedListing } from '@/src/lib/api/endpoints/listing';

type ListingHeaderActionsProps = {
  listing: PropertyDetailListing;
  showCTAs?: boolean;
  presentation?: 'responsive' | 'mobileFloating';
  mobileFloatingVariant?: 'onMedia' | 'onSurface';
  /** Legacy prop kept for back-compat; the new pill layout uses fixed dimensions. */
  size?: 'sm' | 'md';
};

/**
 * ListingHeaderActions — the top-right action cluster on the desktop HDP
 * sticky header. Composes Save / Share icon-with-label buttons and (when
 * `showCTAs` is true) "Book tour" / "Message" pill CTAs.
 */
export const ListingHeaderActions = ({
  listing,
  showCTAs,
  mobileFloatingVariant = 'onMedia',
  presentation = 'responsive',
}: ListingHeaderActionsProps) => {
  const t = useTranslations('hdp');
  const { listingSaved, toggleSave, identityResolved } = useListingSaveActions(listing);
  const { openShare, shareFormOverlay } = useShareAction(listing as ExtendedListing | null | undefined);
  const { ctas, overlays } = useListingCTAs(listing);
  const headerCTAs = showCTAs ? ctas : [];
  const shareLabel = t('share');
  const shareAriaLabel = t('shareThisListing');
  const saveLabel = t('save');
  const saveAriaLabel = listingSaved ? t('removeFromSaved') : t('saveThisListing');

  if (presentation === 'mobileFloating') {
    const isOnSurface = mobileFloatingVariant === 'onSurface';
    const mobileFloatingTone = isOnSurface ? 'neutral' : 'neutral-fixed';
    const mobileFloatingEmphasis = isOnSurface ? 'outlined' : 'filled';
    const mobileFloatingClassName = cx(headerIconButton, !isOnSurface && mobileHeaderMediaButton);

    return (
      <>
        <IconButton
          type="button"
          shape="circle"
          tone={mobileFloatingTone}
          emphasis={mobileFloatingEmphasis}
          size="xs"
          onImpact={!isOnSurface}
          title={shareLabel}
          aria-label={shareAriaLabel}
          className={mobileFloatingClassName}
          onClick={openShare}
          icon={<LucideIcon icon={Share} />}
        />

        <IconButton
          type="button"
          shape="circle"
          tone={mobileFloatingTone}
          emphasis={mobileFloatingEmphasis}
          size="xs"
          onImpact={!isOnSurface}
          title={saveLabel}
          aria-label={saveAriaLabel}
          className={mobileFloatingClassName}
          onClick={toggleSave}
          disabled={!identityResolved}
          aria-busy={!identityResolved}
          aria-pressed={listingSaved}
          icon={<LucideIcon icon={Heart} fill={listingSaved ? 'currentColor' : 'none'} />}
        />

        {showCTAs && overlays}
        {shareFormOverlay}
      </>
    );
  }

  const shareAction = (
    <IconButton
      type="button"
      shape="circle"
      tone="neutral"
      emphasis="bare"
      size="sm"
      icon={<LucideIcon icon={Share} size="sm" />}
      className={headerActionButton}
      onClick={openShare}
      aria-label={shareAriaLabel}
      title={shareLabel}
    />
  );

  const saveAction = (
    <IconButton
      type="button"
      shape="circle"
      tone="neutral"
      emphasis="bare"
      size="sm"
      icon={<LucideIcon icon={Heart} size="sm" fill={listingSaved ? 'currentColor' : 'none'} />}
      className={headerActionButton}
      onClick={toggleSave}
      disabled={!identityResolved}
      aria-busy={!identityResolved}
      aria-pressed={listingSaved}
      aria-label={saveAriaLabel}
      title={saveLabel}
    />
  );

  return (
    <>
      {shareAction}
      {saveAction}

      {headerCTAs.map((cta) => {
        return (
          <Button
            key={cta.id}
            type="button"
            tone="brand"
            emphasis={cta.emphasis}
            size="sm"
            icon={cta.icon}
            className={headerCtaButton}
            onClick={cta.onClick}
            disabled={cta.disabled}
            aria-busy={cta.ariaBusy}
            aria-label={cta.ariaLabel}
            title={cta.label}
            // Server-rendered, so HTTP monitors can assert it. Distinct from InlineCTAs'
            // `button-<id>` so the two never collide once both are mounted.
            data-testid={`hdp-header-cta-${cta.id}`}
          >
            {cta.label}
          </Button>
        );
      })}

      {showCTAs && overlays}
      {shareFormOverlay}
    </>
  );
};
